import { EditOutlined, UnorderedListOutlined } from "@ant-design/icons"
import { Button, Col, Flex, Form, Row, Select, Tooltip } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type BaseOptionType } from "antd/lib/select"
import React, { useEffect, useState } from "react"

import {
  AddressAutoComplete,
  type SuggestionAddress,
  type SuggestionCityZip
} from "../../../../../components/AutoComplete/AddressAutoComplete.tsx"
import { ZipLookupHint } from "../../../../../components/AutoComplete/ZipLookupHint.tsx"
import { placeholders } from "../../../../../constants/placeholders"
import { suggestionZip } from "../../../../../components/AutoComplete/suggestion-zip.ts"
import { useKnownStates } from "../../../../../hooks/use-known-states.ts"
import { useZipLookup } from "../../../../../hooks/use-zip-lookup.ts"
import { useZipModeToggle } from "../../../../../hooks/use-zip-mode-toggle.ts"
import { validateZipCode } from "../../../../../utils/validation/zip-code.ts"
import type { CustomerPick, ProjectFormData } from "../project-form-data.ts"
import { ProjectAddressMapPreview } from "../shared/ProjectAddressMapPreview.tsx"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

interface CustomerAddressSectionProps {
  form: FormInstance<ProjectFormData>
  saving?: boolean
  options: Record<string, BaseOptionType[]>
  initialZip?: string | null
  customerPick?: CustomerPick | null
}

export function CustomerAddressSection({
  form,
  saving,
  options,
  initialZip,
  customerPick
}: CustomerAddressSectionProps) {
  const key: keyof ProjectFormData = "customer"

  const [zipOptions, setZipOptions] = useState<SuggestionCityZip[]>([])

  const status = Form.useWatch([key, "status"], form)

  const isActiveCustomer = status === "Active"
  const isDisabled = saving || isActiveCustomer
  const hasMultipleZips = zipOptions?.length > 1

  const knownStates = useKnownStates(options)
  const {
    lookupZip,
    checkCity,
    chooseMatch,
    declineCityMismatch,
    suppressLookupFor,
    clearStatus,
    status: zipStatus
  } = useZipLookup({
    knownStates,
    disabled: isDisabled,
    initialZip,
    onFill: (fill) => form.setFieldsValue({ [key]: fill })
  })

  const { isCustomZip, toggleZipMode } = useZipModeToggle(
    form,
    [key, "zip_code"],
    clearStatus
  )

  useEffect(() => {
    if (customerPick) suppressLookupFor(customerPick.zip)
  }, [customerPick, suppressLookupFor])

  const onAddressSelect = ({ address, city_zips }: SuggestionAddress) => {
    const zip = suggestionZip({ address, city_zips })

    suppressLookupFor(zip)
    form.setFieldsValue({
      [key]: {
        address: address?.street || null,
        city: address?.city || null,
        state: address?.state || null,
        zip_code: zip
      }
    })
    checkCity(zip, address?.city)

    setZipOptions(city_zips)
  }

  const onZipCodeSelect = (zipCode: string) => {
    const selectedZip = zipOptions.find((z) => z.zip_code === zipCode)
    if (selectedZip) {
      suppressLookupFor(selectedZip?.zip_code)
      form.setFieldsValue({
        [key]: {
          zip_code: selectedZip?.zip_code,
          city: selectedZip?.city,
          state: selectedZip?.state
        }
      })
    }
  }

  return (
    <>
      <Form.Item<ProjectFormData> label="Address Search">
        <AddressAutoComplete onSelect={onAddressSelect} disabled={isDisabled} />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectFormData>
            label="Address 1"
            name={[key, "address"]}
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Please enter address"
              }
            ]}
          >
            <TextInput disabled={isDisabled} placeholder="Enter address" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectFormData>
            label="Address 2"
            name={[key, "address_2"]}
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Please enter address"
              }
            ]}
          >
            <TextInput disabled={isDisabled} placeholder="Enter address 2" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={10}>
          <Form.Item<ProjectFormData>
            label="City"
            name={[key, "city"]}
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Please enter city"
              }
            ]}
          >
            <TextInput
              disabled={isDisabled}
              placeholder="Enter city"
              onChange={(event) =>
                checkCity(
                  form.getFieldValue([key, "zip_code"]),
                  event.target.value
                )
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={10}>
          <Form.Item<ProjectFormData>
            label="State"
            name={[key, "state"]}
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Please enter state"
              }
            ]}
            normalize={(value: string) => value ?? null}
          >
            <ResolvedSelect
              allowClear
              disabled={isDisabled}
              placeholder="Enter state"
              options={options?.["state"]}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={4}>
          <Form.Item<ProjectFormData> label="ZIP Code">
            <Flex gap={"small"}>
              <Form.Item<ProjectFormData>
                name={[key, "zip_code"]}
                noStyle
                rules={[
                  {
                    required: false,
                    validator: validateZipCode
                  }
                ]}
              >
                {hasMultipleZips && !isCustomZip ? (
                  <Select
                    style={{ flex: 1 }}
                    allowClear
                    disabled={isDisabled}
                    placeholder={placeholders.zipCode}
                    onSelect={onZipCodeSelect}
                    options={zipOptions.map((z) => ({
                      value: z.zip_code,
                      label: z.zip_code
                    }))}
                  />
                ) : (
                  <TextInput
                    disableAutoCapitalize
                    style={{ flex: 1 }}
                    disabled={isDisabled}
                    placeholder={placeholders.zipCode}
                    onChange={(event) => lookupZip(event.target.value)}
                  />
                )}
              </Form.Item>

              {hasMultipleZips && (
                <Tooltip
                  placement={"topLeft"}
                  title={isCustomZip ? "Show list" : "Enter custom"}
                >
                  <Button
                    icon={
                      isCustomZip ? <UnorderedListOutlined /> : <EditOutlined />
                    }
                    onClick={toggleZipMode}
                    disabled={isDisabled}
                  />
                </Tooltip>
              )}
            </Flex>
            <ZipLookupHint
              status={zipStatus}
              onChoose={chooseMatch}
              onDecline={declineCityMismatch}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Map Preview */}
      <ProjectAddressMapPreview form={form} isCustomerSection />
    </>
  )
}
